import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from '../../axios/requests';
import API from '../../constant.api';

export type BranchStatus = 'active' | 'deactive';

// none: no approval, manager / admin: always that queue,
// tiered: up to managerLimit goes to the manager, beyond it to the admin
export type ApprovalMode = 'none' | 'manager' | 'admin' | 'tiered';

export interface ApprovalRule {
  mode: ApprovalMode;
  managerLimit: number;
}

export type TieredRuleKey =
  | 'loanDirectDocumentCharge'
  | 'loanTakeoverDocumentCharge'
  | 'loanRepledgeDocumentCharge'
  | 'loanAdditionalAmount'
  | 'saleDocumentCharge'
  | 'saleAdditionalAmount';

// payments and pledges have a single approval queue: only 'none' or 'admin'
export type SingleQueueRuleKey =
  | 'pledgeBelowMinGoldRate'
  | 'loanPaymentInterestReduction'
  | 'pledgePaymentExtra';

export type DocumentChargeRateKey =
  'loanDirect' | 'loanTakeover' | 'loanRepledge' | 'sale';

export interface ApprovalWorkflow {
  // percent of the principal
  documentChargeRates: Record<DocumentChargeRateKey, number>;
  rules: Record<TieredRuleKey | SingleQueueRuleKey, ApprovalRule>;
}

export interface CreateBranchPayload {
  name: string;
  code: string;
  address: string;
  email?: string;
  phoneNumber1?: string;
  phoneNumber2?: string;
}

export interface UpdateBranchPayload {
  name?: string;
  code?: string;
  address?: string;
  email?: string;
  phoneNumber1?: string;
  phoneNumber2?: string;
  status?: BranchStatus;
  approvalWorkflow?: ApprovalWorkflow;
}

export interface BranchResponse {
  _id: string;
  name: string;
  code: string;
  address: string;
  email?: string;
  phoneNumber1?: string;
  phoneNumber2?: string;
  organization: string;
  status?: BranchStatus;
  // the code appears in loans, customers, deposits, pledges... numbers
  codeInUse?: boolean;
  // false for the only active branch: at least one branch must stay active
  canDeactivate?: boolean;
  canDelete?: boolean;
  canEditCode?: boolean;
}

// Superadmin only. requests.ts attaches the Bearer token.
export const createBranch = async (payload: CreateBranchPayload) => {
  const { data } = await postRequest(API.BRANCHES, payload);
  return data;
};

export const fetchBranches = async (): Promise<BranchResponse[]> => {
  const { data } = await getRequest(API.BRANCHES, {});
  return data;
};

export const fetchDefaultApprovalWorkflow =
  async (): Promise<ApprovalWorkflow> => {
    const { data } = await getRequest(
      `${API.BRANCHES}/approval-workflow/defaults`,
      {}
    );
    return data;
  };

// Any signed-in role of the organization can read it (the loan and sale forms use it)
export const fetchBranchApprovalWorkflow = async (
  id: string
): Promise<ApprovalWorkflow> => {
  const { data } = await getRequest(
    `${API.BRANCHES}/${id}/approval-workflow`,
    {}
  );
  return data;
};

export const updateBranch = async (
  id: string,
  payload: UpdateBranchPayload
) => {
  const { data } = await putRequest(`${API.BRANCHES}/${id}`, payload);
  return data;
};

export const deleteBranch = async (id: string) => {
  const { data } = await deleteRequest(`${API.BRANCHES}/${id}`);
  return data;
};

// The login response's branch list goes stale after adding/editing a branch, so reload it.
// 'branches' (read by the dropdowns across the app) keeps only the active branches, in the
// same shape as the login response ({ id, ...branch }); 'branchCount' is the total, so the app
// knows an organization with every branch deactivated still has branches.
export const storeBranches = (branches: BranchResponse[]): void => {
  window.localStorage.setItem(
    'branches',
    JSON.stringify(
      branches
        .filter((branch) => branch.status !== 'deactive')
        .map((branch) => ({ ...branch, id: branch._id }))
    )
  );
  window.localStorage.setItem('branchCount', String(branches.length));
};

export const refreshStoredBranches = async (): Promise<BranchResponse[]> => {
  const branches = await fetchBranches();
  storeBranches(branches);
  return branches;
};
